/**
 * VÉRAULT Portal — Content Studio · AI Assistant
 *
 * The studio briefing room. It does NOT write finished content here — the
 * inline AI in the journal editor handles that. This surface helps the
 * advisor decide WHAT to write next.
 *
 * Three tools, three surfaces:
 *   1. Studio Brief — auto-analysis of recent rhythm + gaps + 3 next pieces.
 *      Every suggestion has an "Draft in editor" button that creates a real
 *      journal draft (via /api/cms/ai/draft-from-outline) and opens it.
 *   2. Series Planner — plan a multi-part series from a theme.
 *   3. Headline Lab — generate alternative headlines for a topic.
 *
 * All AI calls use the established Emergent LLM Key + GPT-5.2 pattern via
 * /api/cms/ai/*. UI keeps the quiet luxury aesthetic.
 */
import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Sparkles,
  RefreshCcw,
  ArrowUpRight,
  Hash,
  Copy,
  Check,
  ListChecks,
  TrendingUp,
  AlertCircle,
  ChevronRight,
} from "lucide-react";
import {
  Surface,
  EditorialRule,
  Empty,
  Skeleton,
  PortalButton,
  api,
} from "../../../index";

function Section({ title, hint, action, children, testid }) {
  return (
    <Surface variant="default" padded className="space-y-5" data-testid={testid}>
      <div className="flex items-end justify-between gap-3">
        <div>
          <h3 className="font-serif text-xl text-[hsl(var(--foreground))]">{title}</h3>
          {hint && (
            <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--foreground-muted))]">
              {hint}
            </p>
          )}
        </div>
        {action}
      </div>
      {children}
    </Surface>
  );
}

function StudioBrief({ onDraft, drafting }) {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);

  const run = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.post("/cms/ai/studio-brief", {});
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Studio brief failed.");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    run();
  }, [run]);

  return (
    <Section
      title="The briefing room"
      hint="A calm read of where the journal stands and what wants writing next"
      testid="assistant-studio-brief"
      action={
        <PortalButton
          variant="ghost"
          size="sm"
          icon={<RefreshCcw className="size-3.5" strokeWidth={1.5} />}
          onClick={run}
          disabled={loading}
          data-testid="assistant-brief-refresh"
        >
          {loading ? "Reading…" : "Refresh"}
        </PortalButton>
      }
    >
      {loading && !data ? (
        <div className="space-y-3">
          <Skeleton className="h-6 w-[70%]" />
          <Skeleton className="h-20" />
          <Skeleton className="h-20" />
        </div>
      ) : error ? (
        <div
          className="font-body text-[13px] text-[hsl(var(--accent-terracotta))] inline-flex items-center gap-2"
          data-testid="assistant-brief-error"
        >
          <AlertCircle className="size-3.5" strokeWidth={1.5} /> {error}
        </div>
      ) : data ? (
        <div className="space-y-7" data-testid="assistant-brief-content">
          {/* Rhythm */}
          {data.rhythm && (
            <div className="flex items-start gap-3">
              <TrendingUp
                className="size-4 mt-0.5 text-[hsl(var(--accent-gold))]"
                strokeWidth={1.5}
              />
              <p className="font-accent italic text-[15px] text-[hsl(var(--foreground-secondary))] leading-relaxed">
                {data.rhythm}
              </p>
            </div>
          )}

          {/* Gaps */}
          {data.gaps?.length > 0 && (
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">
                Gaps to consider
              </p>
              <div className="mt-2 flex flex-wrap gap-1.5" data-testid="assistant-brief-gaps">
                {data.gaps.map((g) => (
                  <span
                    key={g}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[hsl(var(--background-paper))] border border-[rgba(0,0,0,0.06)] font-mono text-[11px] tracking-tight text-[hsl(var(--foreground-secondary))]"
                  >
                    {g}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Suggestions */}
          {data.suggestions?.length > 0 && (
            <div>
              <EditorialRule label={`${data.suggestions.length} suggested next pieces`} />
              <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
                {data.suggestions.map((s, i) => (
                  <div
                    key={i}
                    className="group rounded border border-[rgba(0,0,0,0.06)] bg-[hsl(var(--background-paper))]/40 p-4 flex flex-col gap-3"
                    data-testid={`assistant-brief-suggestion-${i}`}
                  >
                    <div>
                      <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--accent-gold))]">
                        {String(i + 1).padStart(2, "0")}
                      </p>
                      <h4 className="mt-1 font-serif text-[15px] text-[hsl(var(--foreground))] leading-snug">
                        {s.title}
                      </h4>
                      {s.angle && (
                        <p className="mt-2 font-body text-[13px] text-[hsl(var(--foreground-secondary))] leading-relaxed line-clamp-3">
                          {s.angle}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center justify-between mt-auto pt-2 border-t border-[rgba(0,0,0,0.04)]">
                      <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))] truncate">
                        {[s.category, s.destination_tag].filter(Boolean).join(" · ")}
                      </div>
                      <button
                        onClick={() =>
                          onDraft({
                            title: s.title,
                            angle: s.angle,
                            key_points: [],
                            destination_tag: s.destination_tag,
                            category: s.category,
                          })
                        }
                        disabled={drafting}
                        data-testid={`assistant-brief-draft-${i}`}
                        className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--accent-gold))] disabled:opacity-40 transition-colors"
                      >
                        Draft in editor <ChevronRight className="size-3" strokeWidth={1.5} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : null}
    </Section>
  );
}

function SeriesPlanner({ onDraft, drafting }) {
  const [theme, setTheme] = React.useState("");
  const [destination, setDestination] = React.useState("");
  const [count, setCount] = React.useState(3);
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);

  const run = async () => {
    if (!theme.trim()) {
      setError("Theme is required.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const r = await api.post("/cms/ai/series-plan", {
        theme: theme.trim(),
        destination: destination.trim(),
        count: Number(count),
      });
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Series plan failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Section
      title="Compose a series"
      hint="Plan a multi-part editorial sequence around a theme"
      testid="assistant-series"
    >
      <div className="grid grid-cols-1 sm:grid-cols-[1.4fr_minmax(120px,1fr)_90px_auto] gap-2.5">
        <input
          type="text"
          value={theme}
          onChange={(e) => setTheme(e.target.value)}
          placeholder="Theme — e.g. Slow autumn in Italy"
          data-testid="assistant-series-theme"
          className="h-10 px-3 text-[14px] bg-[hsl(var(--background-paper))] border border-[rgba(0,0,0,0.08)] rounded focus:outline-none focus:border-[hsl(var(--accent-gold))]"
        />
        <input
          type="text"
          value={destination}
          onChange={(e) => setDestination(e.target.value)}
          placeholder="Destination (optional)"
          data-testid="assistant-series-destination"
          className="h-10 px-3 text-[14px] bg-[hsl(var(--background-paper))] border border-[rgba(0,0,0,0.08)] rounded focus:outline-none focus:border-[hsl(var(--accent-gold))]"
        />
        <input
          type="number"
          min={2}
          max={6}
          value={count}
          onChange={(e) => setCount(e.target.value)}
          data-testid="assistant-series-count"
          className="h-10 px-3 text-[14px] bg-[hsl(var(--background-paper))] border border-[rgba(0,0,0,0.08)] rounded focus:outline-none focus:border-[hsl(var(--accent-gold))]"
        />
        <PortalButton
          variant="primary"
          size="md"
          icon={<Sparkles className="size-3.5" strokeWidth={1.5} />}
          onClick={run}
          disabled={loading}
          data-testid="assistant-series-generate"
        >
          {loading ? "Composing…" : "Plan it"}
        </PortalButton>
      </div>

      {error && (
        <div
          className="font-body text-[13px] text-[hsl(var(--accent-terracotta))] inline-flex items-center gap-2"
          data-testid="assistant-series-error"
        >
          <AlertCircle className="size-3.5" strokeWidth={1.5} /> {error}
        </div>
      )}

      {loading && !data && <Skeleton className="h-32" />}

      {data && (
        <div className="space-y-4" data-testid="assistant-series-output">
          <div className="space-y-1.5">
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">
              Series
            </p>
            <h4 className="font-serif text-xl text-[hsl(var(--foreground))]">
              {data.series_title}
            </h4>
            {data.series_blurb && (
              <p className="font-accent italic text-[13.5px] text-[hsl(var(--foreground-secondary))]">
                {data.series_blurb}
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {(data.outlines || []).map((o, i) => (
              <div
                key={i}
                className="rounded border border-[rgba(0,0,0,0.06)] bg-[hsl(var(--background-paper))]/40 p-4 space-y-3"
                data-testid={`assistant-series-outline-${i}`}
              >
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--accent-gold))]">
                    Part {String(i + 1).padStart(2, "0")}
                  </p>
                  <h5 className="mt-1 font-serif text-[15px] text-[hsl(var(--foreground))] leading-snug">
                    {o.title}
                  </h5>
                  {o.angle && (
                    <p className="mt-1.5 font-accent italic text-[12.5px] text-[hsl(var(--foreground-secondary))]">
                      {o.angle}
                    </p>
                  )}
                </div>
                {o.key_points?.length > 0 && (
                  <ul className="space-y-1 ml-1">
                    {o.key_points.map((kp, k) => (
                      <li
                        key={k}
                        className="flex items-start gap-1.5 font-body text-[12.5px] text-[hsl(var(--foreground-secondary))] leading-relaxed"
                      >
                        <ListChecks
                          className="size-3 mt-1 shrink-0 text-[hsl(var(--foreground-muted))]"
                          strokeWidth={1.5}
                        />
                        <span>{kp}</span>
                      </li>
                    ))}
                  </ul>
                )}
                <div className="flex items-center justify-between pt-2 border-t border-[rgba(0,0,0,0.04)]">
                  <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))] truncate">
                    {[o.category, o.destination_tag].filter(Boolean).join(" · ")}
                  </div>
                  <button
                    onClick={() => onDraft(o)}
                    disabled={drafting}
                    data-testid={`assistant-series-draft-${i}`}
                    className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--accent-gold))] disabled:opacity-40 transition-colors"
                  >
                    Draft in editor <ChevronRight className="size-3" strokeWidth={1.5} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </Section>
  );
}

function HeadlineLab() {
  const [topic, setTopic] = React.useState("");
  const [count, setCount] = React.useState(6);
  const [headlines, setHeadlines] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [copiedIdx, setCopiedIdx] = React.useState(null);

  const run = async () => {
    if (!topic.trim()) {
      setError("Topic is required.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const r = await api.post("/cms/ai/headline-ideas", {
        topic: topic.trim(),
        count: Number(count),
      });
      setHeadlines(r.data.headlines || []);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Headline ideas failed.");
    } finally {
      setLoading(false);
    }
  };

  const copy = async (text, idx) => {
    try {
      await navigator.clipboard?.writeText(text);
    } catch {
      // Some sandboxed/iframe contexts deny clipboard permission. Swallow
      // the rejection — the visual confirmation below still gives feedback.
    }
    setCopiedIdx(idx);
    setTimeout(() => setCopiedIdx(null), 1400);
  };

  return (
    <Section
      title="Headline lab"
      hint="Quick alternatives for any article topic"
      testid="assistant-headlines"
    >
      <div className="grid grid-cols-1 sm:grid-cols-[1.6fr_90px_auto] gap-2.5">
        <input
          type="text"
          value={topic}
          onChange={(e) => setTopic(e.target.value)}
          placeholder="Topic — e.g. truffle season in Piedmont"
          data-testid="assistant-headlines-topic"
          className="h-10 px-3 text-[14px] bg-[hsl(var(--background-paper))] border border-[rgba(0,0,0,0.08)] rounded focus:outline-none focus:border-[hsl(var(--accent-gold))]"
        />
        <input
          type="number"
          min={3}
          max={12}
          value={count}
          onChange={(e) => setCount(e.target.value)}
          data-testid="assistant-headlines-count"
          className="h-10 px-3 text-[14px] bg-[hsl(var(--background-paper))] border border-[rgba(0,0,0,0.08)] rounded focus:outline-none focus:border-[hsl(var(--accent-gold))]"
        />
        <PortalButton
          variant="primary"
          size="md"
          icon={<Hash className="size-3.5" strokeWidth={1.5} />}
          onClick={run}
          disabled={loading}
          data-testid="assistant-headlines-generate"
        >
          {loading ? "Composing…" : "Compose"}
        </PortalButton>
      </div>

      {error && (
        <div
          className="font-body text-[13px] text-[hsl(var(--accent-terracotta))] inline-flex items-center gap-2"
          data-testid="assistant-headlines-error"
        >
          <AlertCircle className="size-3.5" strokeWidth={1.5} /> {error}
        </div>
      )}

      {loading && !headlines && (
        <div className="space-y-2">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-10" />
          ))}
        </div>
      )}

      {headlines && headlines.length > 0 && (
        <ul className="divide-y divide-[rgba(0,0,0,0.04)]" data-testid="assistant-headlines-list">
          {headlines.map((h, i) => (
            <li
              key={i}
              className="group flex items-center justify-between gap-3 py-2.5"
              data-testid={`assistant-headlines-item-${i}`}
            >
              <span className="font-serif text-[15px] text-[hsl(var(--foreground))] leading-snug min-w-0 truncate">
                {h}
              </span>
              <button
                onClick={() => copy(h, i)}
                title="Copy"
                data-testid={`assistant-headlines-copy-${i}`}
                className="shrink-0 size-8 inline-flex items-center justify-center rounded text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))] hover:bg-[hsl(var(--background-paper))]"
              >
                {copiedIdx === i ? (
                  <Check className="size-3.5 text-[hsl(var(--accent-sage))]" strokeWidth={1.5} />
                ) : (
                  <Copy className="size-3.5" strokeWidth={1.5} />
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

export default function ContentAssistant() {
  const navigate = useNavigate();
  const [drafting, setDrafting] = React.useState(false);
  const [toast, setToast] = React.useState(null);

  const handleDraft = async (outline) => {
    setDrafting(true);
    try {
      const r = await api.post("/cms/ai/draft-from-outline", outline);
      const id = r.data?.id;
      setToast(`"${r.data?.title || outline.title}" created · opening editor…`);
      setTimeout(() => {
        setToast(null);
        if (id) navigate(`/advisor/content/journal/${id}`);
      }, 900);
    } catch (e) {
      window.alert(
        `Couldn't create the draft: ${e?.response?.data?.detail || e.message || "unknown error"}`,
      );
      setDrafting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="content-assistant">
      <StudioBrief onDraft={handleDraft} drafting={drafting} />
      <SeriesPlanner onDraft={handleDraft} drafting={drafting} />
      <HeadlineLab />

      <div className="pt-1">
        <Empty
          title="Finished writing lives inside the editor."
          hint="When you open a draft, the inline AI is one keystroke away — these tools are just for deciding what to write next."
          icon={<ArrowUpRight className="size-5" strokeWidth={1.25} />}
        />
      </div>

      {toast && (
        <div
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-5 py-3 rounded-full bg-[hsl(var(--foreground))] text-[hsl(var(--primary-foreground))] shadow-2xl font-mono text-[11px] uppercase tracking-[0.18em]"
          data-testid="content-assistant-toast"
        >
          {toast}
        </div>
      )}
    </div>
  );
}
